/**
 * Server Actions for AI Scribe Recordings
 *
 * Recordings are a top-level resource — the URL is flat (`/recordings`) and
 * `matter_id` is nullable at the schema layer (foundation for the orphan-
 * recording follow-up in LF-3712). The matter scope is supplied via:
 *  - request body for `POST /recordings` and `POST /recordings/audio-upload-url`
 *  - `?matterId=<n>` query param on `GET /recordings`
 *  - implicit (derived from the recording row) for everything else.
 *
 * `revalidatePath` calls dropped — invalidation now happens via React Query
 * at the hook layer (see hooks.ts + query-dependencies.ts `recording`).
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import type { ActionResult } from '@/lib/types/actions';
import type {
  Recording,
  RecordingDetail,
  RecordingListResponse,
  RecordingListParams,
  ApplyChangesRequest,
  ApplyChangesResponse,
  ConsolidationProgress,
  AudioUploadUrlRequest,
  AudioUploadUrlResponse,
  AudioFormat,
  CreateRecordingRequest,
  UploadCompleteRequest,
  UpdateRecordingRequest
} from './types';
import { safeStringify } from '@/lib/utils/safe-stringify';

// Preview/apply debug logs — local dev only. Server-action logs land in the
// Next.js dev server terminal, not the browser console.
const DEBUG = process.env.NODE_ENV === 'development';

function log(action: string, phase: string, data: unknown) {
  if (!DEBUG) return;
  console.log(`[recordings] ${action} / ${phase}:\n${safeStringify(data)}`);
}

const RECORDINGS_PATH = '/recordings';

// ─── List ───────────────────────────────────────────────────────────

/**
 * Firm-wide / matter-scoped recording list for the top-level Scribe surface
 * (LF-3712). Supports full-text search (`q`), lifecycle `status` filtering, the
 * `archived` view, and `?matterId` scoping. Default scope is firm-wide, active
 * (non-archived), recent-first; with `q`, results are relevance-ranked server-side.
 */
export async function getRecordingsListAction(
  params: RecordingListParams = {}
): Promise<ActionResult<RecordingListResponse>> {
  try {
    const data = await directApiClient.get<RecordingListResponse>(
      `${RECORDINGS_PATH}${buildRecordingListQuery(params)}`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch recordings'
    };
  }
}

/** Serialise list params to a `?a=b&c=d` query string (empty when no params). */
function buildRecordingListQuery(params: RecordingListParams): string {
  const search = new URLSearchParams();
  if (params.q?.trim()) search.set('q', params.q.trim());
  if (params.status?.length) search.set('status', params.status.join(','));
  if (params.archived) search.set('archived', 'true');
  if (params.matterId != null) search.set('matterId', String(params.matterId));
  if (params.mode) search.set('mode', params.mode);
  if (params.page != null) search.set('page', String(params.page));
  if (params.limit != null) search.set('limit', String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ─── Detail ─────────────────────────────────────────────────────────

export async function getRecordingDetailAction(
  recordingId: number
): Promise<ActionResult<RecordingDetail>> {
  try {
    const data = await directApiClient.get<RecordingDetail>(
      `${RECORDINGS_PATH}/${recordingId}`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch recording'
    };
  }
}

// ─── Audio Upload URL ──────────────────────────────────────────────

export async function getAudioUploadUrlAction(
  request: AudioUploadUrlRequest
): Promise<ActionResult<AudioUploadUrlResponse>> {
  try {
    const data = await directApiClient.post<AudioUploadUrlResponse>(
      `${RECORDINGS_PATH}/audio-upload-url`,
      request
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to get audio upload URL'
    };
  }
}

/**
 * Re-key an existing draft recording for a pre-recorded audio file upload
 * (`POST /recordings/:id/audio-upload-url`). Returns a presigned PUT URL for the
 * recording's own audio key, re-pointed to the file's format. Used by the
 * top-level "upload a file" path where the draft row already exists.
 */
export async function getRecordingAudioUploadUrlAction(
  recordingId: number,
  audioFormat: AudioFormat
): Promise<ActionResult<AudioUploadUrlResponse>> {
  try {
    const data = await directApiClient.post<AudioUploadUrlResponse>(
      `${RECORDINGS_PATH}/${recordingId}/audio-upload-url`,
      { audioFormat }
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to get audio upload URL'
    };
  }
}

// ─── Create ─────────────────────────────────────────────────────────

export async function createRecordingAction(
  request: CreateRecordingRequest
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      RECORDINGS_PATH,
      request
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to create recording'
    };
  }
}

// ─── Start ──────────────────────────────────────────────────────────

/**
 * Signal that the lawyer initiated capture ("Start recording"). Flips the
 * backend row `draft → started` and emits RECORDING_STARTED. Idempotent on the
 * backend (a replay past `draft` is a no-op), so callers can fire it best-effort
 * without worrying about double-invocation. Desktop-only path — the mobile
 * handoff has its own start via the device-recording BFF (LF-4841).
 */
export async function startRecordingAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/start`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to start recording'
    };
  }
}

// ─── Discard Capture ────────────────────────────────────────────────

/**
 * Signal that the lawyer abandoned an in-progress capture before it uploaded
 * (LF-5144, top-level + matter scribe). Flips the backend row `started →
 * draft` and emits RECORDING_DISCARDED. Idempotent on the backend (a replay
 * past `started` is a no-op), so callers can fire it best-effort.
 */
export async function discardCaptureAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/discard-capture`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to discard capture'
    };
  }
}

// ─── Upload Complete ────────────────────────────────────────────────

export async function uploadCompleteAction(
  recordingId: number,
  request: UploadCompleteRequest
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/upload-complete`,
      request
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to signal upload complete'
    };
  }
}

// ─── Regenerate Upload URL ──────────────────────────────────────────

export async function regenerateUploadUrlAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/upload-url`,
      {}
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to regenerate upload URL'
    };
  }
}

// ─── Apply Changes ──────────────────────────────────────────────────

export async function applyChangesAction(
  recordingId: number,
  request: ApplyChangesRequest
): Promise<ActionResult<ApplyChangesResponse>> {
  log('applyChangesAction', 'REQUEST', {
    recordingId,
    endpoint: `POST ${RECORDINGS_PATH}/${recordingId}/apply-changes`,
    payload: request
  });

  try {
    const data = await directApiClient.post<ApplyChangesResponse>(
      `${RECORDINGS_PATH}/${recordingId}/apply-changes`,
      request
    );

    log('applyChangesAction', 'RESPONSE', data);

    return { success: true, data };
  } catch (error) {
    log('applyChangesAction', 'ERROR', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to apply changes'
    };
  }
}

// ─── Consolidate ───────────────────────────────────────────────────

export async function consolidateRecordingAction(
  recordingId: number,
  intakeFormIds?: string[]
): Promise<ActionResult<ConsolidationProgress>> {
  try {
    const data = await directApiClient.post<ConsolidationProgress>(
      `${RECORDINGS_PATH}/${recordingId}/consolidate`,
      intakeFormIds?.length ? { intakeFormIds } : {}
    );

    return { success: true, data };
  } catch (error) {
    // 409 means consolidation already in progress — treat as "keep polling"
    const msg =
      error instanceof Error ? error.message : 'Failed to consolidate';
    if (msg.includes('409') || msg.includes('already in progress')) {
      return {
        success: true,
        data: {
          status: 'processing',
          overallProgress: 0,
          consolidationSteps: [],
          message: null
        }
      };
    }
    return { success: false, error: msg };
  }
}

// ─── Consolidation Progress ────────────────────────────────────────

export async function getConsolidationProgressAction(
  recordingId: number
): Promise<ActionResult<ConsolidationProgress>> {
  try {
    const data = await directApiClient.get<ConsolidationProgress>(
      `${RECORDINGS_PATH}/${recordingId}/consolidation-progress`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch consolidation progress'
    };
  }
}

// ─── Retry ──────────────────────────────────────────────────────────

export async function retryRecordingAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/retry`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to retry recording'
    };
  }
}

// ─── Re-analyse ─────────────────────────────────────────────────────

export async function reanalyseRecordingAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/reanalyse`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to reanalyse recording'
    };
  }
}

// ─── Regenerate summary (VALE-480) ──────────────────────────────────

/**
 * Re-generate **only** the note against a different note template.
 *
 * Narrower than reanalyse: the summary sections are rewritten, and
 * `aiExtractedData` — with every entity-card edit the lawyer has made — is left
 * alone. The pick is persisted server-side, so it also survives a later
 * re-analysis. Status goes `processing` → `ready`, exactly like reanalyse, so
 * the page's existing poll carries it back.
 */
export async function regenerateRecordingSummaryAction(
  recordingId: number,
  templateId: string
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/regenerate-summary`,
      { templateId }
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to regenerate the note'
    };
  }
}

// ─── Update ─────────────────────────────────────────────────────────

export async function updateRecordingAction(
  recordingId: number,
  request: UpdateRecordingRequest
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.patch<Recording>(
      `${RECORDINGS_PATH}/${recordingId}`,
      request
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update recording'
    };
  }
}

// ─── Dismiss ────────────────────────────────────────────────────────

export async function dismissRecordingAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/dismiss`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to dismiss recording'
    };
  }
}

// ─── Undismiss ──────────────────────────────────────────────────────

export async function undismissRecordingAction(
  recordingId: number
): Promise<ActionResult<Recording>> {
  try {
    const data = await directApiClient.post<Recording>(
      `${RECORDINGS_PATH}/${recordingId}/undismiss`,
      {}
    );

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to undismiss recording'
    };
  }
}

// ─── Delete ─────────────────────────────────────────────────────────

export async function deleteRecordingAction(
  recordingId: number
): Promise<ActionResult<{ deleted: true }>> {
  try {
    await directApiClient.delete(`${RECORDINGS_PATH}/${recordingId}`);
    return { success: true, data: { deleted: true } };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to delete recording'
    };
  }
}
